/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {VoidEventHandler} from '@ion/core/src/types/general';
import {BulkOperationResults} from '@ion/core/src/types/services';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {ExportButton} from '../editors/controls/ExportButton';
import {dlsColor, dlsTypography} from '../theme/theme';
import {DLSTheme} from '../types/types';
import {partialStatusMessageFormatter} from '../utils/ComponentUtils';

// defines the prop definitions for the bulk import results notification ...
export type BulkImportResultsNotificationProps = {
  icon?: ReactElement;
  capitalize?: boolean;
  show: boolean;
  valid: boolean;
  isError: boolean;
  title: string;
  actionLabel?: string | JSX.Element;
  dismissLabel?: string;
  entityLabel: string;
  result: BulkOperationResults;
  errors?: boolean;
  onExport: (errorsOnly: boolean) => void;
  onAction?: VoidEventHandler;
  onDismiss?: VoidEventHandler;
  itemsCount?: number;
};

// defines the styles used by this component ...
const useStyles = makeStyles<DLSTheme, BulkImportResultsNotificationProps>(({dls}) => ({
  icon : {
    display       : 'inline-block',
    verticalAlign : 'top',
    paddingTop    : 2,
    marginRight   : 6
  },
  paper : {
    borderRadius : 0,
    width        : '100%'
  },
  title : {
    ...dlsTypography('h4'),
    textTransform : ({capitalize}) => capitalize ? 'uppercase' : 'none',
    padding       : '20px 20px 0px 24px',
    lineHeight    : '24px',
    letterSpacing : 1
  },
  results : {
    ...dlsTypography('body1'),
    margin : 0
  },
  content : {
    padding : '0 24px !important',
    margin  : '10px 0 15px'
  },
  actions : {
    justifyContent : 'flex-start',
    padding        : '0px 16px 8px !important'
  },
  exportWrapper : {
    display    : 'flex',
    '& button' : {
      padding : '0 10px 0 0'
    },
    '& button:hover' : {
      textDecorationColor : dlsColor('grey3')
    },
    '& [class*=label]' : {
      ...dlsTypography('body1')
    }
  }
}));

/**
 * The BulkImportResultsNotification displays the results of bulk imports with
 * success and errors segregated via excel.
 *
 * ```typescript
 *
 *   import {BulkImportResultsNotification} from '@ion/dls/src/imports';
 *
 *   <BulkImportResultsNotification show={show}
 *     capitalize
 *     title={translate('common:editors.import.results.title', {entityLabel})}
 *     actionLabel={translate('common:editors.import.results.onConfirm')}
 *     dismissLabel={translate('common:editors.import.results.onRestart')}
 *     entityLabel={entityLabel}
 *     isError={isError}
 *     result={result}
 *     onExport={onExport}
 *     onAction={onConfirm}
 *     onDismiss={onRestart}
 *     valid={valid} />
 * ```
 *
 * @param icon if any to be shown based on the results of imports
 * @param capitalize if true sets the dialog title in capital case
 * @param show toggles the display of results prompt
 * @param title the results prompt header
 * @param valid checks if results have invalid entities which helps in generating error excel
 * data
 * @param isError checks if import failed
 * @param actionLabel the label for button which confirms the current import results
 * @param dismissLabel the label for button which starts a fresh import
 * @param entityLabel the current CTCEntity label under bulk import
 * @param result The result object which has results/errors post import
 * completion
 * @param errors checks if import results have errors
 * @param onExport callback to be executed if user begins export
 * @param onAction callback to execute when import results are generated and user confirms
 * the same
 * @param onDismiss callback to execute when user wants to start a fresh import
 */
export const BulkImportResultsNotification = (props: BulkImportResultsNotificationProps) => {
  const {
    icon,
    show,
    title,
    valid,
    isError,
    actionLabel,
    dismissLabel,
    entityLabel,
    result,
    errors,
    onExport,
    onAction,
    onDismiss,
    itemsCount = 0
  } = props;
  const classes = useStyles(props);

  function getStatusTxt() {
    const success    = result?.results || [];
    const failures   = result?.errors || [];
    const total      = [...success, ...failures];
    return partialStatusMessageFormatter(success, total);
  }

  if (show) {
    const hasActions = Boolean(onAction || onDismiss);

    return (
      <Dialog classes={{paper: classes.paper}} open>
        <DialogTitle className={classes.title}>
          {icon && <div className={classes.icon}>{icon}</div>} {title}
        </DialogTitle>
        <DialogContent className={classes.content}>
          <p className={classes.results}>
            {!isError
              ? translate('common:editors.import.results.message', {entityLabel, statusTxt: itemsCount || getStatusTxt()})
              : <span>{`${translate('common:errors.generic')}. ${translate('common:tryAgain')}`}</span>}
          </p>
          <div className={classes.exportWrapper}>
            {result?.results && <ExportButton uppercase={false} label={translate('common:editors.import.results.downloadResults')} onExport={function() {
              onExport(false);
            }} />}
            {!valid && !isError && <ExportButton uppercase={false} label={translate('common:editors.import.results.downloadErrors')} onExport={function() {
              onExport(true);
            }} />}
          </div>
        </DialogContent>
        {hasActions &&
          <DialogActions className={classes.actions}>
            {onAction &&
              <TertiaryButton style={{padding: 0}} onClick={onAction}>
                {actionLabel}
              </TertiaryButton>}
            {onDismiss &&
              <TertiaryButton onClick={onDismiss} isAlt altColor={dlsColor('grey3')}>
                {dismissLabel}
              </TertiaryButton>}
          </DialogActions>}
      </Dialog>
    );
  }
  return null;
};
export default BulkImportResultsNotification;
